import db, { type Cotacao, type User } from '@/db';
import { supabase } from '@/services/supabaseClient';

// Cotações vivem no Supabase (tabela zsb_cotacoes, RLS por dono/gerente/admin).
// O Dexie local só é usado como reserva: se a gravação na nuvem falhar, a cotação
// fica no navegador e sobe sozinha na próxima abertura (syncLocalCotacoes).

interface CotacaoRow { id: number; data: Omit<Cotacao, 'id'>; created_at: string }

const PAGE_SIZE = 1000;
export const COTACOES_CHANGED_EVENT = 'zsb-cotacoes-change';
export const notifyCotacoesChanged = () => window.dispatchEvent(new Event(COTACOES_CHANGED_EVENT));

const mapRow = (row: CotacaoRow): Cotacao => ({ ...row.data, id: row.id, createdAt: row.data.createdAt ?? row.created_at });
const normalize = (value?: string) => (value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
const localKey = (cotacao: Cotacao) => `${cotacao.id}-${cotacao.createdAt}`;

async function ownEmail(): Promise<string | null> {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('zsb_own_email');
  return error || !data ? null : String(data);
}

function rowPayload(owner: string, cotacao: Omit<Cotacao, 'id'>, key: string | null) {
  return {
    owner, local_key: key, cliente_codigo: cotacao.clienteCodigo, cliente_nome: cotacao.clienteNome,
    vendedor: cotacao.vendedor, divisao: cotacao.divisao ?? null, moeda: cotacao.moeda, total: cotacao.total,
    total_brl: cotacao.totalBrl, margem_media: cotacao.margemMedia, data: cotacao, created_at: cotacao.createdAt,
  };
}

export async function listCotacoes(): Promise<Cotacao[]> {
  const pending = await db.cotacoes.toArray(); // reserva local ainda não enviada (ou sem Supabase)
  if (!supabase) return pending.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const all: Cotacao[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await supabase.from('zsb_cotacoes').select('id,data,created_at')
      .order('created_at', { ascending: false }).order('id', { ascending: false }).range(from, from + PAGE_SIZE - 1);
    if (error) { console.error('Não foi possível carregar as cotações da nuvem:', error); break; }
    all.push(...(data as CotacaoRow[]).map(mapRow));
    if (data.length < PAGE_SIZE) break;
  }
  return [...all, ...pending.filter((item) => item.id >= 0).map((item) => ({ ...item, pendingSync: true }))].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export interface SavedCotacao { cotacao: Cotacao; remote: boolean }

export async function saveCotacao(cotacao: Omit<Cotacao, 'id'>): Promise<SavedCotacao> {
  try {
    const owner = await ownEmail();
    if (!supabase || !owner) throw new Error('sem sessão na nuvem');
    const { data, error } = await supabase.from('zsb_cotacoes').insert(rowPayload(owner, cotacao, null)).select('id,data,created_at').single();
    if (error) throw error;
    notifyCotacoesChanged();
    return { cotacao: mapRow(data as CotacaoRow), remote: true };
  } catch (error) {
    console.error('Cotação salva só neste navegador (falha ao gravar na nuvem):', error);
    const id = await db.cotacoes.add(cotacao as Cotacao);
    notifyCotacoesChanged();
    return { cotacao: { ...cotacao, id } as Cotacao, remote: false };
  }
}

// Exclusão lógica no servidor (dono, gerente da equipe ou admin). Cotação que ainda está
// só no navegador é removida daqui mesmo.
export async function deleteCotacao(cotacao: Cotacao): Promise<string | null> {
  if (cotacao.pendingSync || !supabase) { await db.cotacoes.delete(cotacao.id); notifyCotacoesChanged(); return null; }
  const { data, error } = await supabase.rpc('zsb_delete_cotacao', { p_id: cotacao.id });
  if (error) return 'Não foi possível excluir a cotação. Tente novamente.';
  if (data !== true) return 'Você não tem permissão para excluir esta cotação.';
  notifyCotacoesChanged();
  return null;
}

const syncedUsers = new Set<string>();

// Sobe para a nuvem as cotações que este usuário criou antes (ou sem conexão) e
// ainda estão só no navegador. É idempotente: (owner, local_key) é único.
export async function syncLocalCotacoes(user: User): Promise<number> {
  if (!supabase || syncedUsers.has(user.email)) return 0;
  syncedUsers.add(user.email);
  try {
    const owner = await ownEmail();
    if (!owner) { syncedUsers.delete(user.email); return 0; }
    const names = [user.name, user.responsibleName].filter(Boolean).map(normalize);
    const mine = (await db.cotacoes.toArray()).filter((item) => item.id >= 0 && names.includes(normalize(item.vendedor)));
    let sent = 0;
    for (let i = 0; i < mine.length; i += 100) {
      const chunk = mine.slice(i, i + 100);
      const { data, error } = await supabase.from('zsb_cotacoes')
        .upsert(chunk.map((item) => { const { id: _id, ...rest } = item; return rowPayload(owner, rest, localKey(item)); }), { onConflict: 'owner,local_key', ignoreDuplicates: true })
        .select('id');
      if (error) throw error;
      sent += data?.length ?? 0;
      await db.cotacoes.bulkDelete(chunk.map((item) => item.id)); // já está na nuvem: a reserva local sai
    }
    if (sent) notifyCotacoesChanged();
    return sent;
  } catch (error) {
    console.error('Não foi possível enviar as cotações locais para a nuvem:', error);
    syncedUsers.delete(user.email);
    return 0;
  }
}
